import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../api';
import { BASE_APP_PAth } from '../../api/launchpad-api';
import { useAuthContext } from '../../contexts/AuthContextProvider';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useCreateChangeRequest, useReferenceCarriers, useReferenceLinesOfBusiness, useReferenceStates } from '../../hooks/useAdminApi';
import {
    AppetiteSetupView,
    BulkTemplateResult,
    ParsedBulkUpload,
    buildTemplateCsv,
    bulkSetupJson,
    parseTemplateUpload,
} from './bulkTemplate';

const ADMIN_BASE = `${BASE_APP_PAth}/admin`;

function actionErrorMessage(e: unknown) {
    const err = e as { response?: { data?: unknown }; message?: string };
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
        const o = data as { message?: string; notices?: { message?: string }[] };
        const nested = o.notices?.map(notice => notice.message).filter(Boolean).join('; ');
        if (nested) return nested;
        if (o.message) return o.message;
    }
    return err?.message || 'Request failed';
}

function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
}

export const BulkOperations = () => {
    const { getUserName, getUserAccount, getAuthToken } = useAuthContext();
    const { canRequestChange } = useAdminAccess();
    const { adminApi } = useApi();
    const createCR = useCreateChangeRequest();
    const { data: carriers } = useReferenceCarriers();
    const { data: lobs } = useReferenceLinesOfBusiness();
    const { data: states } = useReferenceStates();

    const [view, setView] = useState<AppetiteSetupView>('classic');
    const [carrierId, setCarrierId] = useState('');
    const [lobId, setLobId] = useState('');
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [preparing, setPreparing] = useState(false);
    const [template, setTemplate] = useState<BulkTemplateResult | null>(null);
    const [upload, setUpload] = useState<ParsedBulkUpload | null>(null);
    const [fileName, setFileName] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);
    const [actionOk, setActionOk] = useState<string | null>(null);

    const ready = carrierId !== '' && lobId !== '' && selectedStates.length > 0;
    const targetTable = view === 'classic' ? 'CarrierRecommendation' : 'CarrierAppetiteByUkw';

    const resetResult = () => {
        setTemplate(null);
        setUpload(null);
        setFileName('');
        setActionError(null);
        setActionOk(null);
    };

    const switchView = (next: AppetiteSetupView) => {
        setView(next);
        resetResult();
    };

    const toggleState = (code: string) => {
        setSelectedStates(current => current.includes(code)
            ? current.filter(item => item !== code)
            : [...current, code]);
        resetResult();
    };

    const prepare = async () => {
        setPreparing(true);
        setActionError(null);
        setActionOk(null);
        setUpload(null);
        setFileName('');
        try {
            const result = await adminApi.getBulkTemplate(
                view, Number(carrierId), Number(lobId), selectedStates.join(','), getAuthToken());
            setTemplate(result);
        } catch (e) {
            setTemplate(null);
            setActionError(actionErrorMessage(e));
        } finally {
            setPreparing(false);
        }
    };

    const download = () => {
        if (!template?.canDownload || template.rows.length === 0) return;
        const safeName = `${template.carrierName}-${template.lineOfBusiness}-${template.view}`
            .replace(/[^\w.-]+/g, '-');
        downloadCsv(`appetite-setup-${safeName}.csv`, buildTemplateCsv(template));
    };

    const onFile = async (file: File | undefined) => {
        setActionError(null);
        setActionOk(null);
        setUpload(null);
        setFileName(file?.name ?? '');
        if (!file || !template?.canDownload) return;
        const text = await file.text();
        setUpload(parseTemplateUpload(text, template));
    };

    const submit = async () => {
        if (!template?.canDownload || !upload || upload.errors.length > 0 || upload.rows.length === 0) return;
        setActionError(null);
        setActionOk(null);
        const states = Array.from(new Set(upload.rows.map(row => row.stateCode))).join(', ');
        const skipped = template.rows.length - upload.rows.length;
        try {
            const result = await createCR.mutateAsync({
                targetTable,
                changeData: { BulkSetup: bulkSetupJson(template, upload.rows) },
                requestedBy: getUserName() ?? 'Agent',
                requestedByEmail: getUserAccount()?.username ?? '',
                comments: `Bulk appetite setup for ${template.carrierName} / ${template.lineOfBusiness} in ${states}: ${upload.rows.length} rows (${upload.activeCount} active, ${upload.inactiveCount} inactive)${skipped > 0 ? `, ${skipped} template rows skipped` : ''}.`,
                autoSubmit: true,
            });
            if (!result.success) throw new Error(result.message || 'Change request was not created');
            const id = result.data?.id;
            setActionOk(id
                ? `Change request CR-${id} was submitted for review.`
                : 'Change request was submitted for review.');
            setUpload(null);
            setFileName('');
        } catch (e) {
            setActionError(actionErrorMessage(e));
        }
    };

    const sample = useMemo(() => upload?.rows.slice(0, 8) ?? [], [upload]);
    const canSubmit = !!template?.canDownload && !!upload && upload.errors.length === 0 && upload.rows.length > 0;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Bulk Upload</h1>
                    <p className="admin-page-subtitle">
                        Download a template for a carrier, line of business, and states that have no appetite yet.
                        Fill the business class and mark each state active or inactive, then submit one change request.
                    </p>
                </div>
            </div>

            <div className="admin-tabs">
                <button type="button" className={`admin-tab${view === 'classic' ? ' active' : ''}`} onClick={() => switchView('classic')}>
                    Classic
                </button>
                <button type="button" className={`admin-tab${view === 'universal' ? ' active' : ''}`} onClick={() => switchView('universal')}>
                    Universal
                </button>
            </div>

            {!canRequestChange && (
                <div className="admin-alert admin-alert-info admin-alert-row" role="status">
                    <span className="admin-icon icon-info" aria-hidden="true" />
                    <div className="admin-alert-body">Your role can view this screen. Submitting a setup requires the requester role.</div>
                </div>
            )}

            <div className="admin-filters">
                <div className="select-box admin-select-wide">
                    <select
                        value={carrierId}
                        aria-label="Carrier"
                        onChange={e => { setCarrierId(e.target.value); resetResult(); }}>
                        <option value="">Select a carrier</option>
                        {carriers?.data.map(carrier => (
                            <option key={carrier.carrierId} value={carrier.carrierId}>{carrier.carrierName}</option>
                        ))}
                    </select>
                </div>
                <div className="select-box">
                    <select
                        value={lobId}
                        aria-label="Line of business"
                        onChange={e => { setLobId(e.target.value); resetResult(); }}>
                        <option value="">Select a line of business</option>
                        {lobs?.data.map(lob => (
                            <option key={lob.lineOfBusinessId} value={lob.lineOfBusinessId}>{lob.lobName}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="admin-state-toolbar">
                <p className="admin-meta-label">States to include</p>
                <button
                    type="button"
                    className="admin-btn admin-btn-sm admin-btn-ghost"
                    onClick={() => { setSelectedStates(states?.data.map(state => state.stateCode) ?? []); resetResult(); }}>
                    Select all
                </button>
                <button
                    type="button"
                    className="admin-btn admin-btn-sm admin-btn-ghost"
                    onClick={() => { setSelectedStates([]); resetResult(); }}>
                    Clear
                </button>
            </div>
            <div className="admin-state-picker">
                {states?.data.map(state => (
                    <label key={state.stateCode} className="admin-check">
                        <input
                            type="checkbox"
                            checked={selectedStates.includes(state.stateCode)}
                            onChange={() => toggleState(state.stateCode)}
                        />
                        <span>{state.stateName}</span>
                    </label>
                ))}
            </div>

            <div className="admin-actions">
                <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    disabled={!ready || preparing || !canRequestChange}
                    onClick={prepare}>
                    {preparing ? <><span className="admin-spin" /> Checking appetite…</> : 'Prepare template'}
                </button>
                <button
                    type="button"
                    className="admin-btn admin-btn-secondary"
                    disabled={!template?.canDownload || template.rows.length === 0}
                    onClick={download}>
                    Download Excel template
                </button>
            </div>

            {actionError && (
                <div className="admin-alert admin-alert-error admin-alert-row" role="alert">
                    <span className="admin-icon icon-alert" aria-hidden="true" />
                    <div className="admin-alert-body">{actionError}</div>
                </div>
            )}
            {actionOk && (
                <div className="admin-alert admin-alert-ok admin-alert-row" role="status">
                    <span className="admin-icon icon-confirmation" aria-hidden="true" />
                    <div className="admin-alert-body">
                        {actionOk}{' '}
                        <Link to={`${ADMIN_BASE}/approval-queue`}>Open the approval queue</Link>
                    </div>
                </div>
            )}

            {template && !template.canDownload && (
                <div className="admin-alert admin-alert-info admin-alert-row" role="status">
                    <span className="admin-icon icon-info" aria-hidden="true" />
                    <div className="admin-alert-body">
                        {template.message}{' '}
                        {template.existingCount > 0 && (
                            <Link to={`${ADMIN_BASE}/appetite-mapping`}>Go to Appetite Mapping</Link>
                        )}
                    </div>
                </div>
            )}

            {template?.canDownload && (
                <section className="admin-card">
                    <h3>Upload the completed template</h3>
                    <p className="admin-muted">
                        {template.rows.length} rows for {template.carrierName}, {template.lineOfBusiness}.
                        Business class is filled in where this carrier already has a mapping.
                        Open the file in Excel, fill Business Class where it is blank, and set Active to 1 or 0.
                        Rows you delete are skipped: those markets are not written to business class or appetite. Save as CSV.
                    </p>
                    <div className="admin-file-row">
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            aria-label="Completed template"
                            onChange={e => onFile(e.target.files?.[0])}
                        />
                        {fileName && <span className="admin-muted">{fileName}</span>}
                    </div>

                    {upload && upload.errors.length > 0 && (
                        <div className="admin-alert admin-alert-error" role="alert">
                            <span className="admin-icon icon-alert" aria-hidden="true" />
                            <div className="admin-alert-body">
                                {upload.errors.slice(0, 8).map(error => <p key={error}>{error}</p>)}
                            </div>
                        </div>
                    )}

                    {upload && upload.warnings.length > 0 && upload.errors.length === 0 && (
                        <div className="admin-alert" role="status">
                            <span className="admin-icon icon-alert" aria-hidden="true" />
                            <div className="admin-alert-body">
                                {upload.warnings.map(warning => <p key={warning}>{warning}</p>)}
                            </div>
                        </div>
                    )}

                    {upload && upload.errors.length === 0 && (
                        <>
                            <p className="admin-meta-value">
                                {upload.rows.length} rows · {upload.activeCount} active · {upload.inactiveCount} inactive
                            </p>
                            <div className="admin-table-scroll">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Keyword</th>
                                            <th>State</th>
                                            <th>Business class</th>
                                            <th>Active</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sample.map(row => (
                                            <tr key={`${row.assocId}-${row.usStateId}`}>
                                                <td>{row.keyword}</td>
                                                <td>{row.stateCode}</td>
                                                <td>{row.businessClass}</td>
                                                <td>{row.weight === 1 ? 'Active' : 'Inactive'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {upload.rows.length > sample.length && (
                                <p className="admin-muted">Showing the first {sample.length} rows.</p>
                            )}
                        </>
                    )}

                    <button
                        type="button"
                        className="admin-btn admin-btn-primary"
                        disabled={!canSubmit || createCR.isPending || !canRequestChange}
                        onClick={submit}>
                        {createCR.isPending ? <><span className="admin-spin" /> Submitting…</> : 'Submit change request'}
                    </button>
                </section>
            )}
        </div>
    );
};
